import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getHubbleDiscounts,
  getStoreApiPayload,
  getStoreProducts,
  getStoreSettings,
  isStoreApiSuccess,
  patchHubbleDiscount,
  patchStoreProduct,
  syncHubbleBrands,
  syncHubbleDiscounts,
} from '../../services/storeApi';
import {
  findProductForDiscount,
  formatInrShort,
  formatPercent,
  getPricingPreview,
  parseDiscountsList,
} from '../Store/storeLabels';
import { useStoreSession } from '../Store/useStoreSession';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const PAGE_SIZE = 100;
const PRODUCT_FETCH_LIMIT = 500;

const SETUP_STEPS = [
  'Sync Hubble brands',
  'Sync Hubble discounts',
  'Enable Listed on the product',
  'Set the discount override',
  'Check the pricing preview',
];

const emptyForm = { overriden_discount: '', is_disabled: false };

const PRICING_SPLIT_ROWS = [
  ['Face value', 'faceValueInr'],
  ['Fan Energy covers', 'fanEnergyCoversInr'],
  ['Cash payable', 'cashPayableInr'],
];

/** Face value split into the Fan Energy-funded discount and the cash the user still pays. */
function PricingPreview({ preview, variant = 'block' }) {
  if (!preview) {
    return (
      <Typography variant="caption" color="text.secondary">
        No catalog pricing
      </Typography>
    );
  }

  const splitLines = (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      {PRICING_SPLIT_ROWS.map(([label, key]) => (
        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={key === 'cashPayableInr' ? 600 : 400}>
            {formatInrShort(preview[key])}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  if (variant === 'inline') {
    return splitLines;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Pricing preview {preview.isProjected && <Chip size="small" label="Projected" sx={{ ml: 1 }} />}
      </Typography>
      {splitLines}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Fan Energy needed
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {preview.fanEnergyRequired != null ? `${preview.fanEnergyRequired.toLocaleString('en-IN')} FE` : '—'}
        </Typography>
      </Box>
      {preview.isProjected && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Estimated from the override above. Save and re-sync to see the catalog values.
        </Typography>
      )}
    </Paper>
  );
}

function StoreDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [editDiscount, setEditDiscount] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fanEnergyPerInr, setFanEnergyPerInr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { showSnackbar, handleSessionExpired } = useStoreSession(setSnackbar);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [discountsRes, productsRes, settingsRes] = await Promise.all([
        getHubbleDiscounts({ limit: PAGE_SIZE, offset }),
        getStoreProducts({ limit: PRODUCT_FETCH_LIMIT, offset: 0 }),
        getStoreSettings(),
      ]);

      if (handleSessionExpired(discountsRes) || handleSessionExpired(productsRes)) return;

      if (isStoreApiSuccess(discountsRes)) {
        setDiscounts(parseDiscountsList(discountsRes));
      } else {
        showSnackbar(discountsRes?.message || 'Failed to load discounts', 'error');
        setDiscounts([]);
      }

      if (isStoreApiSuccess(productsRes)) {
        setProducts(getStoreApiPayload(productsRes)?.products || []);
      } else {
        setProducts([]);
      }

      setFanEnergyPerInr(
        isStoreApiSuccess(settingsRes) ? getStoreApiPayload(settingsRes)?.fan_energy_per_inr ?? null : null
      );
    } finally {
      setLoading(false);
    }
  }, [offset, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const rows = useMemo(
    () =>
      discounts.map((discount) => ({
        discount,
        product: findProductForDiscount(products, discount),
      })),
    [discounts, products]
  );

  const filteredRows = useMemo(() => {
    let list = rows;
    if (statusFilter === 'OVERRIDDEN') {
      list = list.filter(({ discount }) => discount.overriden_discount != null);
    } else if (statusFilter === 'NO_OVERRIDE') {
      list = list.filter(({ discount }) => discount.overriden_discount == null);
    } else if (statusFilter === 'UNLISTED') {
      list = list.filter(({ discount }) => !discount.store_product_listed);
    } else if (statusFilter === 'DISABLED') {
      list = list.filter(({ discount }) => !!discount.is_disabled);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        ({ discount }) =>
          String(discount.store_product_title || '').toLowerCase().includes(q) ||
          String(discount.brand_id || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, statusFilter, search]);

  const runSync = async (kind) => {
    setSyncing(kind);
    try {
      const res = kind === 'brands' ? await syncHubbleBrands() : await syncHubbleDiscounts();
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Sync failed', 'error');
        return;
      }
      const synced = getStoreApiPayload(res)?.synced;
      const noun = kind === 'brands' ? 'brand' : 'discount';
      showSnackbar(synced != null ? `Synced ${synced} ${noun}(s)` : `Hubble ${noun}s synced`);
      loadData();
    } finally {
      setSyncing('');
    }
  };

  const handleListProduct = async (product) => {
    setSaving(true);
    try {
      const res = await patchStoreProduct(product.id, { is_listed: true, is_active: true });
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to list product', 'error');
        return;
      }
      showSnackbar('Product listed and activated');
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const openEdit = ({ discount }) => {
    setEditDiscount(discount);
    setForm({
      overriden_discount: discount.overriden_discount != null ? String(discount.overriden_discount) : '',
      is_disabled: !!discount.is_disabled,
    });
  };

  const editProduct = useMemo(
    () => (editDiscount ? findProductForDiscount(products, editDiscount) : null),
    [editDiscount, products]
  );

  const editPreview = useMemo(() => {
    if (!editProduct) return null;
    const raw = form.overriden_discount.trim();
    // An empty field means "fall back to the Hubble default", so preview that rather than the saved effective rate.
    const percent = raw === '' ? editDiscount?.default_discount ?? null : Number(raw);
    const preview = getPricingPreview(editProduct, { discountPercent: percent, fanEnergyPerInr });
    return { ...preview, isProjected: preview.discountPercent !== editDiscount?.effective_discount_percent };
  }, [editProduct, editDiscount, form.overriden_discount, fanEnergyPerInr]);

  const submitDiscount = async (body, successMessage) => {
    setSaving(true);
    try {
      const res = await patchHubbleDiscount(editDiscount.brand_id, body);
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Update failed', 'error');
        return;
      }
      showSnackbar(successMessage);
      setEditDiscount(null);
      setForm(emptyForm);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editDiscount) return;
    const raw = form.overriden_discount.trim();
    let overriden_discount = null;

    if (raw !== '') {
      const num = Number(raw);
      if (!Number.isFinite(num) || num < 0 || num > 100) {
        showSnackbar('Override discount must be between 0 and 100', 'warning');
        return;
      }
      overriden_discount = num;
    }

    await submitDiscount({ overriden_discount, is_disabled: !!form.is_disabled }, 'Discount saved');
  };

  const handleClearOverride = async () => {
    if (!editDiscount) return;
    await submitDiscount({ overriden_discount: null }, 'Override cleared');
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Hubble Discounts</h3>
                  <Typography variant="body2" color="text.secondary">
                    Brand-level default discounts, admin overrides and the resulting Fan Energy split
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button component={Link} to="/admin/store" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                        Store dashboard
                      </Button>
                      <Button component={Link} to="/admin/store/products" variant="outlined" size="small">
                        Products
                      </Button>
                      <Button variant="contained" onClick={() => runSync('brands')} disabled={!!syncing || loading}>
                        {syncing === 'brands' ? 'Syncing brands…' : 'Sync brands'}
                      </Button>
                      <Button variant="contained" onClick={() => runSync('discounts')} disabled={!!syncing || loading}>
                        {syncing === 'discounts' ? 'Syncing discounts…' : 'Sync discounts'}
                      </Button>
                      <TextField
                        select
                        size="small"
                        label="Filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 170 }}
                      >
                        <MenuItem value="ALL">All brands</MenuItem>
                        <MenuItem value="OVERRIDDEN">Has override</MenuItem>
                        <MenuItem value="NO_OVERRIDE">No override</MenuItem>
                        <MenuItem value="UNLISTED">Not listed</MenuItem>
                        <MenuItem value="DISABLED">Disabled</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        label="Search product or brand ID"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 240 }}
                      />
                      <Button variant="text" onClick={loadData} disabled={loading}>
                        Refresh
                      </Button>
                    </Box>

                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Recommended admin flow
                      </Typography>
                      <Stepper activeStep={-1} alternativeLabel sx={{ mt: 1 }}>
                        {SETUP_STEPS.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </Paper>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        {products.length === 0 && discounts.length > 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Catalog products could not be loaded, so pricing previews are unavailable.
                          </Alert>
                        )}

                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell>Brand ID</TableCell>
                                <TableCell align="right">Default discount</TableCell>
                                <TableCell align="right">Override discount</TableCell>
                                <TableCell align="right">Effective discount</TableCell>
                                <TableCell>Pricing preview</TableCell>
                                <TableCell>Listed</TableCell>
                                <TableCell>Disabled</TableCell>
                                <TableCell align="right">Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredRows.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={9} align="center">
                                    No discounts found — sync Hubble brands and discounts first
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredRows.map((row) => {
                                  const { discount, product } = row;
                                  return (
                                    <TableRow key={discount.brand_id} hover>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                          {discount.store_product_title || '—'}
                                        </Typography>
                                        {product && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Product #{product.id}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>{discount.brand_id}</TableCell>
                                      <TableCell align="right">{formatPercent(discount.default_discount)}</TableCell>
                                      <TableCell align="right">
                                        {discount.overriden_discount != null ? (
                                          <Chip size="small" color="primary" label={formatPercent(discount.overriden_discount)} />
                                        ) : (
                                          '—'
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight={600}>
                                          {formatPercent(discount.effective_discount_percent)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <PricingPreview
                                          preview={
                                            product
                                              ? getPricingPreview(product, {
                                                  discountPercent: discount.effective_discount_percent,
                                                  fanEnergyPerInr,
                                                })
                                              : null
                                          }
                                          variant="inline"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {discount.store_product_listed ? (
                                          <Chip size="small" color="success" label="Listed" />
                                        ) : (
                                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <Chip size="small" label="Hidden" />
                                            {product && (
                                              <Button size="small" onClick={() => handleListProduct(product)} disabled={saving}>
                                                List
                                              </Button>
                                            )}
                                          </Box>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          color={discount.is_disabled ? 'error' : 'default'}
                                          label={discount.is_disabled ? 'Disabled' : 'Enabled'}
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <Button size="small" onClick={() => openEdit(row)}>
                                          Edit discount
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                            Previous
                          </Button>
                          <Button disabled={discounts.length < PAGE_SIZE} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                            Next
                          </Button>
                          <Typography variant="body2" sx={{ alignSelf: 'center', ml: 1 }}>
                            Offset {offset}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={!!editDiscount} onClose={() => setEditDiscount(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit discount — {editDiscount?.store_product_title || `Brand ${editDiscount?.brand_id}`}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Brand ${editDiscount?.brand_id}`} />
                <Chip size="small" label={`Default ${formatPercent(editDiscount?.default_discount)}`} />
                <Chip
                  size="small"
                  color="primary"
                  label={`Effective ${formatPercent(editDiscount?.effective_discount_percent)}`}
                />
              </Box>

              <TextField
                label="Override discount percentage"
                type="number"
                value={form.overriden_discount}
                onChange={(e) => setForm((f) => ({ ...f, overriden_discount: e.target.value }))}
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText="0–100. Leave empty to fall back to the Hubble default discount."
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_disabled}
                    onChange={(e) => setForm((f) => ({ ...f, is_disabled: e.target.checked }))}
                  />
                }
                label="Disabled"
              />

              {editProduct ? (
                <PricingPreview preview={editPreview} />
              ) : (
                <Alert severity="info">
                  No catalog product matched this brand, so the pricing preview is unavailable.
                </Alert>
              )}

              {editDiscount && !editDiscount.store_product_listed && (
                <Alert
                  severity="warning"
                  action={
                    editProduct ? (
                      <Button size="small" onClick={() => handleListProduct(editProduct)} disabled={saving}>
                        List now
                      </Button>
                    ) : null
                  }
                >
                  This product is not listed yet, so users cannot see it.
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
            <Button
              color="error"
              onClick={handleClearOverride}
              disabled={saving || editDiscount?.overriden_discount == null}
            >
              Clear override
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setEditDiscount(null)}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default StoreDiscounts;
